// Important:
// No need for window. prefix or import I18n from 'i18n-js'
// Client: I18n will be taken from window object
// Server: Execution context contains override for I18n functions (rails)
declare let I18n: any // lets typescript know that I18n was already declared

interface GetI18nTextParams {
  id: string
  defaultValue?: string
  count?: number | string
  map?: (value: string) => string[]
  [key: string]: any
}

/**
 * Get the translation text by ID
 *
 * @param {string} id translation ID
 * @param {string} defaultValue value returned if no translation is found by ID
 * @param {number | string} count Count can be used both for pluralization and interpolation.
 * @param {(value: string) => string[]} map method of converting the text obtained with the ID
 *
 * @return {string} translated text
 *
 * @example <caption>Example usage of getI18nText.</caption>
 *
 * const userName = getI18nText({id: 'user.name', defaultValue: 'My name is {name}', name: 'John Doe'})
 * //return:   userName -> My name is John Doe
 * */
export function getI18nText<RT = string>({
  id,
  defaultValue,
  count,
  map,
  ...values
}: GetI18nTextParams): RT {
  const t = I18n.t(id, { defaultValue, count, ...values })

  return map ? map(t) : t
}

/**
 * Get formatted number
 *
 * @param {number} value number value
 *
 * @return {string} formatted number
 *
 * */
export function getI18nNumber(value: number): string {
  return I18n.l('number', value)
}

/**
 * Get the formatted to currency number
 *
 * @param {number} value The value is the property to set the numeric value to be formatted.
 * @param {string} unit The currency in which the number will be formatted. (by default '€')
 * @param {number} precision Precision value for number (by default 0)
 * @param {string} delimiter
 * @param {string} format
 *
 * @return {string} formatted number
 *
 * @example <caption>Example usage of getI18nPrice.</caption>
 *
 * const price = getI18nPrice({value: 10, unit: '$', precision: 2})
 * //return:   price -> $10.00
 * */
export function getI18nPrice({
  value,
  unit = '€',
  precision = 0,
  delimiter = I18n.delimiter,
  format = I18n.format,
}: {
  value: number | string
  unit?: string
  precision?: number
  delimiter?: string
  format?: string
}): string {
  return I18n.toCurrency(value, {
    precision,
    unit,
    delimiter,
    format,
  })
}

/**
 * Get the formatted to locate date
 *
 * @param {string | number | Date} date The date is the property to set the date value to be formatted.
 * @param {'short' | 'long'} monthLength MonthLength is a property for setting the month length when formatting (by default: 'short')
 * @param {Intl.DateTimeFormatOptions} options An object that contains one or more properties that specify comparison options.
 *
 * @returns {string} Formatted to locate date
 * @example <caption>Example monthLength short/long</caption>
 *
 * const short = toLocaleDate(Date.now(), 'short') -> '10 Jan 2023'
 * const long = toLocaleDate(Date.now(), 'long') -> '10 January 2023'
 * */
export function toLocaleDate(
  date: string | number | Date,
  monthLength: 'short' | 'long' = 'short',
  options?: Intl.DateTimeFormatOptions
) {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: monthLength,
    year: 'numeric',
  }

  return new Date(date).toLocaleDateString(I18n.locale, options || defaultOptions)
}

export function nameForPeriod(months: number, options?: { short?: boolean; nospace?: boolean }) {
  options = options || {}
  const space = options.nospace ? '' : ' '
  if (months % 12 === 0) {
    const years = months / 12
    return `${years}${space}${
      years === 1 ? periodLabel('year', options.short) : periodLabel('years', options.short)
    }`
  }
  return `${months}${space}${
    months === 1 ? periodLabel('month', options.short) : periodLabel('months', options.short)
  }`
}

function periodLabel(name: string, isShort?: boolean) {
  return getI18nText({ id: `period.${isShort ? 'short' : 'normal'}.${name}` })
}

export function getI18nError({ fieldName, error, defaultValue, errorArgs = {} }: I18nErrorProps) {
  const fieldNameTranslation = getI18nText({
    id: `activemodel.attributes.${fieldName}`,
  })

  const errorTranslation = getI18nText({
    id: `errors.messages.${error}`,
    ...errorArgs,
  })

  const translationResult = `${fieldNameTranslation} ${errorTranslation}`
  const isTranslationResultValid = !translationResult.includes('[missing')

  if (isTranslationResultValid) return translationResult

  return defaultValue || translationResult
}

/**
 * Get the formatted to 'human' number
 *
 * @param {number} value number value to be formatted.
 * @param {number} precision Precision value for number (by default: 3)
 * @param {object} units object with short units, by default: {thousand: 'K',million: 'M', ...}
 *
 * @returns {string} Formatted to 'human' number
 * @example <caption>Example numberToHuman </caption>
 *
 * const formattedNumber = numberToHuman(100_000, 0) -> '100K'
 * const formattedNumber2 = numberToHuman(123_456_789, 2) -> '123.46M'
 * */
export function numberToHuman(
  value: number,
  precision = 3,
  units = {
    thousand: getI18nText({
      id: 'number.human.decimal_units.shorthand.thousand',
    }),
    million: getI18nText({
      id: 'number.human.decimal_units.shorthand.million',
    }),
    billion: getI18nText({
      id: 'number.human.decimal_units.shorthand.billion',
    }),
    trillion: getI18nText({
      id: 'number.human.decimal_units.shorthand.trillion',
    }),
  }
) {
  if (value === 0 || typeof value !== 'number') return 0

  const numberUnits = {
    1_000: 'thousand',
    1_000_000: 'million',
    1_000_000_000: 'billion',
    1_000_000_000_000: 'trillion',
  }

  const getHumanUnit = (unit) => units[numberUnits[unit]]
  const getCurrentUnit = (units, value) => {
    const filteredUnits = units.filter((unit) => Number(unit) <= value)

    return filteredUnits[filteredUnits.length - 1] || units[0]
  }

  const localValue = getLocaleNumber(value)

  const unitsKeys = Object.keys(numberUnits)
  const currentUnit = getCurrentUnit(unitsKeys, localValue)

  const result = localValue / Number(currentUnit)

  return Number(result.toFixed(precision)) + getHumanUnit(currentUnit)
}

//  Chinese language uses 10,000 as base unit instead of 1,000
const getLocaleNumber = (value: number) => {
  let result = value

  if (I18n.locale === 'zh-cn') {
    if (result < 1_000_000) result /= 10
  }
  return result
}
